import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image, Spinner } from 'react-bootstrap';
import logo from '../../assets/images/acumenVelocityLogo.svg';
import { api } from '../../redux/apis/api';
import { useAuth } from '../../context/AuthContext';
import successToast from '../../components/toasts/successToast';
import errorToast from '../../components/toasts/errorToast';

const RESEND_COOLDOWN = 60;

const VerifyTwoFA = () => {
    const navigate = useNavigate();
    const { fetchMe } = useAuth();
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!sessionStorage.getItem('2fa_session_token')) {
            navigate('/');
        }
    }, [navigate]);

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const session_token = sessionStorage.getItem('2fa_session_token');
        if (!session_token) {
            errorToast("Session expired. Please login again.");
            navigate('/');
            return;
        }
        if (!otp || otp.length !== 6) {
            errorToast("Please enter a valid 6-digit OTP.");
            return;
        }

        setLoading(true);
        try {
            const res = await api.post('/auth/verify-2fa', { otp, session_token }, { _isAuthCheck: true });
            if (res?.data?.status) {
                sessionStorage.removeItem('2fa_session_token');
                successToast(res.data.message);
                localStorage.setItem("active_tab", 'Overview');
                await fetchMe();
                navigate('/over-view');
            } else {
                errorToast(res?.data?.message || "Invalid OTP. Please try again.");
            }
        } catch (_) {
            errorToast("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        const session_token = sessionStorage.getItem('2fa_session_token');
        if (!session_token) {
            errorToast("Session expired. Please login again.");
            navigate('/');
            return;
        }

        setResendLoading(true);
        try {
            const res = await api.post('/auth/resend-2fa', { session_token }, { _isAuthCheck: true });
            if (res?.data?.status) {
                const new_session_token = res.data.data?.session_token;
                if (new_session_token) {
                    sessionStorage.setItem('2fa_session_token', new_session_token);
                }
                successToast("New OTP sent to your email!");
                setOtp('');
                setCountdown(RESEND_COOLDOWN);
                clearInterval(timerRef.current);
                timerRef.current = setInterval(() => {
                    setCountdown(prev => {
                        if (prev <= 1) {
                            clearInterval(timerRef.current);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } else {
                errorToast(res?.data?.message || "Failed to resend OTP.");
            }
        } catch (_) {
            errorToast("Something went wrong. Please try again.");
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className='min-vh-100 d-flex flex-column' style={{ backgroundColor: '#E9EEF7' }}>
            <div className='d-flex align-items-center cursor_pointer p-2' style={{ height: '8vh' }} onClick={() => navigate('/')}>
                <Image alt='logo' src={logo} height={40} className='mx-2' />
                <p className='name fs_18 fw_600 m-0'>Acumen Translation Hub</p>
            </div>
            <div className='flex-grow-1 d-flex flex-column justify-content-center align-items-center fn_Roboto'>
                <div className='bg-light border d-flex flex-column justify-content-center align-items-start p-4 rounded-2 gap-2 col-12 col-md-8 col-lg-4'>
                    <h4 className='fw_500 fn_Roboto mb-1'>Two-Factor Authentication</h4>
                    <p className='text-muted fs_14 mb-3'>Enter the 6-digit OTP sent to your email address.</p>
                    <form onSubmit={handleSubmit} className='w-100'>
                        <div className='text-start gap-3 mb-3 w-100'>
                            <label htmlFor='otp' className='py-2'>One-Time Password</label>
                            <input
                                id='otp'
                                type='text'
                                inputMode='numeric'
                                maxLength={6}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                placeholder='Enter 6-digit OTP'
                                className='input-field w-100'
                                style={{ letterSpacing: '4px', fontSize: '20px' }}
                                disabled={loading}
                                autoFocus
                            />
                        </div>
                        {loading && (
                            <div className='d-flex align-items-center mb-3'>
                                <Spinner size='sm' className='text-primary' />
                                <span className='ms-2'>Verifying...</span>
                            </div>
                        )}
                        <div className='mb-2'>
                            {countdown > 0 ? (
                                <span className='text-muted fs_13'>Resend OTP in {countdown}s</span>
                            ) : (
                                <button
                                    type='button'
                                    className='btn btn-link p-0 fs_13'
                                    onClick={handleResend}
                                    disabled={resendLoading}
                                >
                                    {resendLoading ? (
                                        <><Spinner size='sm' /> Sending...</>
                                    ) : (
                                        'Resend OTP'
                                    )}
                                </button>
                            )}
                        </div>
                        <div className='d-flex justify-content-between mt-4'>
                            <button type='submit' className='btn btn-primary' disabled={loading || otp.length !== 6}>
                                Verify OTP
                            </button>
                            <button type='button' className='btn btn-outline-secondary' disabled={loading} onClick={() => { sessionStorage.removeItem('2fa_session_token'); navigate('/'); }}>
                                Back to Login
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default VerifyTwoFA;
